'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

const ZAPIER_WEBHOOK_URL = process.env.NEXT_PUBLIC_ZAPIER_WEBHOOK_URL || '';
const APOLLO_STAGE_ID = process.env.NEXT_PUBLIC_APOLLO_STAGE_ID || '69b85ebb15379d0011ebcbe6';

interface FormData {
  firstName: string;
  lastName: string;
  businessName: string;
  businessWebsite: string;
  industry: string;
  monthlyVolume: string;
  phone: string;
  email: string;
  additionalNotes: string;
}

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  businessName: '',
  businessWebsite: '',
  industry: '',
  monthlyVolume: '',
  phone: '',
  email: '',
  additionalNotes: '',
};

async function sendToZapier(data: FormData): Promise<void> {
  if (!ZAPIER_WEBHOOK_URL) return;
  try {
    await fetch(ZAPIER_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, source: 'barbers-apply-page', submittedAt: new Date().toISOString() }),
    });
  } catch { /* non-blocking */ }
}

async function sendToApollo(data: FormData): Promise<void> {
  try {
    await fetch('/api/apollo-contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        businessName: data.businessName,
        stageId: APOLLO_STAGE_ID,
      }),
    });
  } catch { /* non-blocking */ }
}

async function sendToSupabase(data: FormData): Promise<void> {
  try {
    const supabase = createClient();
    await supabase.from('leads').insert({
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      business_name: data.businessName,
      business_website: data.businessWebsite || null,
      industry: data.industry,
      monthly_volume: data.monthlyVolume,
      notes: data.additionalNotes || null,
      source_page: 'barbers-apply-page',
      status: 'new',
    });
  } catch { /* non-blocking */ }
}

export default function ApplyPage() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.businessName.trim() !== '' &&
    formData.industry !== '' &&
    formData.monthlyVolume !== '' &&
    formData.phone.trim() !== '' &&
    formData.email.trim() !== '';

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.industry) newErrors.industry = 'Please select your industry';
    if (!formData.monthlyVolume) newErrors.monthlyVolume = 'Please select monthly volume';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Enter a valid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    await Promise.allSettled([
      sendToZapier(formData),
      sendToApollo(formData),
      sendToSupabase(formData),
    ]);
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <>
      <style>{`
        .ap-body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #fff;
          color: #111827;
          -webkit-font-smoothing: antialiased;
          margin: 0;
          padding: 0;
        }
        /* NAV */
        .ap-nav {
          position: sticky;
          top: 0;
          z-index: 50;
          background: #fff;
          border-bottom: 1px solid #E5E7EB;
        }
        .ap-nav-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .ap-nav-logo {
          height: 32px;
          width: auto;
        }
        .ap-nav-right {
          display: flex;
          align-items: center;
          gap: 24px;
        }
        .ap-nav-phone {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ap-nav-phone:hover { color: #1d4ed8; }
        .ap-nav-btn {
          background: #1d4ed8;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          padding: 9px 20px;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s;
        }
        .ap-nav-btn:hover { background: #1e40af; }

        /* HERO */
        .ap-hero {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1d4ed8 100%);
          padding: 72px 24px 80px;
        }
        .ap-hero-inner {
          max-width: 1200px;
          margin: 0 auto;
        }
        .ap-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 6px 14px;
          border-radius: 100px;
          margin-bottom: 24px;
        }
        .ap-hero-badge-dot {
          width: 6px;
          height: 6px;
          background: #34d399;
          border-radius: 50%;
        }
        .ap-hero-h1 {
          font-size: clamp(36px, 5vw, 60px);
          font-weight: 800;
          color: #fff;
          line-height: 1.08;
          letter-spacing: -0.03em;
          margin: 0 0 20px;
          max-width: 700px;
        }
        .ap-hero-sub {
          font-size: 18px;
          color: rgba(255,255,255,0.72);
          line-height: 1.6;
          max-width: 560px;
          margin: 0;
        }

        /* MAIN CONTENT */
        .ap-content {
          background: #f8fafc;
          padding: 56px 24px 80px;
        }
        .ap-content-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr;
          gap: 40px;
        }
        @media (min-width: 1024px) {
          .ap-content-inner {
            grid-template-columns: 3fr 2fr;
            gap: 48px;
          }
        }

        /* FORM CARD */
        .ap-form-card {
          background: #fff;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          padding: 40px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
        }
        .ap-form-title {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 6px;
          letter-spacing: -0.02em;
        }
        .ap-form-subtitle {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 32px;
          line-height: 1.5;
        }
        .ap-form-section-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #94a3b8;
          margin: 0 0 16px;
          padding-bottom: 10px;
          border-bottom: 1px solid #f1f5f9;
        }
        .ap-form-group {
          margin-bottom: 20px;
        }
        .ap-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
        @media (max-width: 600px) {
          .ap-form-row { grid-template-columns: 1fr; }
          .ap-form-card { padding: 24px; }
        }
        .ap-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        .ap-input {
          width: 100%;
          padding: 11px 14px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          background: #fff;
          color: #0f172a;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s;
          box-sizing: border-box;
          -webkit-appearance: none;
          appearance: none;
        }
        .ap-input:focus {
          border-color: #1d4ed8;
          box-shadow: 0 0 0 3px rgba(29,78,216,0.12);
        }
        .ap-input.error { border-color: #ef4444; }
        .ap-input::placeholder { color: #94a3b8; }
        select.ap-input {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 36px;
          cursor: pointer;
        }
        textarea.ap-input { resize: vertical; min-height: 100px; }
        .ap-error-msg {
          font-size: 12px;
          color: #ef4444;
          margin-top: 4px;
        }
        .ap-submit-btn {
          width: 100%;
          padding: 15px 24px;
          background: #1d4ed8;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          font-family: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 14px rgba(29,78,216,0.3);
          margin-top: 8px;
        }
        .ap-submit-btn:hover:not(:disabled) {
          background: #1e40af;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(29,78,216,0.4);
        }
        .ap-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .ap-form-legal {
          font-size: 12px;
          color: #94a3b8;
          text-align: center;
          margin-top: 14px;
          line-height: 1.5;
        }
        .ap-form-legal a { color: #1d4ed8; text-decoration: none; }
        .ap-form-legal a:hover { text-decoration: underline; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .ap-spinner { animation: spin 0.75s linear infinite; }

        /* SUCCESS STATE */
        .ap-success {
          text-align: center;
          padding: 20px 0;
        }
        .ap-success-icon {
          width: 72px;
          height: 72px;
          background: #dcfce7;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          color: #16a34a;
        }
        .ap-success-h2 {
          font-size: 26px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.02em;
          margin: 0 0 10px;
        }
        .ap-success-p {
          font-size: 15px;
          color: #475569;
          line-height: 1.6;
          max-width: 420px;
          margin: 0 auto 28px;
        }
        .ap-success-steps {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px 24px;
          text-align: left;
          margin-bottom: 20px;
        }
        .ap-success-steps-title {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 14px;
        }
        .ap-success-step {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 13px;
          color: #475569;
          margin-bottom: 10px;
          line-height: 1.5;
        }
        .ap-success-step:last-child { margin-bottom: 0; }
        .ap-success-check {
          flex-shrink: 0;
          margin-top: 2px;
          color: #1d4ed8;
        }

        /* SIDEBAR */
        .ap-sidebar { display: flex; flex-direction: column; gap: 20px; }

        /* WHAT HAPPENS NEXT CARD */
        .ap-next-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .ap-next-card-title {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 22px;
          letter-spacing: -0.01em;
        }
        .ap-step {
          display: flex;
          gap: 14px;
          margin-bottom: 20px;
        }
        .ap-step:last-child { margin-bottom: 0; }
        .ap-step-num {
          width: 32px;
          height: 32px;
          min-width: 32px;
          background: #1d4ed8;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
        }
        .ap-step-title {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 3px;
        }
        .ap-step-desc {
          font-size: 13px;
          color: #64748b;
          line-height: 1.5;
        }

        /* QUESTIONS CARD */
        .ap-q-card {
          background: #1e3a8a;
          border-radius: 16px;
          padding: 28px;
        }
        .ap-q-card-title {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 10px;
        }
        .ap-q-card-p {
          font-size: 13px;
          color: rgba(255,255,255,0.72);
          line-height: 1.6;
          margin: 0 0 18px;
        }
        .ap-q-card-phone {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #fff;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          transition: color 0.2s;
        }
        .ap-q-card-phone:hover { color: #93c5fd; }

        /* TRUST BAR */
        .ap-trust-bar {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ap-trust-bar-icon { color: #16a34a; flex-shrink: 0; }
        .ap-trust-bar-text { font-size: 13px; color: #374151; line-height: 1.4; }
        .ap-trust-bar-text strong { color: #0f172a; }

        /* FOOTER */
        .ap-footer {
          background: #0f172a;
          padding: 40px 24px;
        }
        .ap-footer-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        @media (min-width: 768px) {
          .ap-footer-inner {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
        }
        .ap-footer-logo { height: 28px; width: auto; filter: brightness(0) invert(1); }
        .ap-footer-links {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          align-items: center;
        }
        .ap-footer-link {
          font-size: 13px;
          color: rgba(255,255,255,0.55);
          text-decoration: none;
          transition: color 0.2s;
        }
        .ap-footer-link:hover { color: #fff; }
        .ap-footer-copy {
          font-size: 12px;
          color: rgba(255,255,255,0.35);
        }
      `}</style>

      <div className="ap-body">

        {/* ── NAV ─────────────────────────────────────────────────────────── */}
        <nav className="ap-nav">
          <div className="ap-nav-inner">
            <a href="/">
              <Image
                src="/assets/images/CHARGEACT_text_logo-1775097726221.png"
                alt="ChargeAct"
                width={140}
                height={32}
                className="ap-nav-logo"
              />
            </a>
            <div className="ap-nav-right">
              <a href="tel:+13237840656" className="ap-nav-phone">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.21.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z" />
                </svg>
                (323) 784-0656
              </a>
            </div>
          </div>
        </nav>

        {/* ── HERO ────────────────────────────────────────────────────────── */}
        <section className="ap-hero">
          <div className="ap-hero-inner">
            <div className="ap-hero-badge">
              <span className="ap-hero-badge-dot" />
              Merchant Application
            </div>
            <h1 className="ap-hero-h1">
              Apply for Better<br />Payment Processing
            </h1>
            <p className="ap-hero-sub">
              Join hundreds of barbershops saving money on every swipe. Fill out the short form below and a dedicated ChargeAct specialist will reach out within 1 business day.
            </p>
          </div>
        </section>

        {/* ── MAIN CONTENT ────────────────────────────────────────────────── */}
        <section className="ap-content">
          <div className="ap-content-inner">

            {/* ── FORM COLUMN ─────────────────────────────────────────────── */}
            <div>
              <div className="ap-form-card">
                {submitted ? (
                  <div className="ap-success">
                    <div className="ap-success-icon">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <h2 className="ap-success-h2">Application Received!</h2>
                    <p className="ap-success-p">
                      Thanks, <strong>{formData.firstName}</strong>! A ChargeAct payment specialist will review your application and reach out within 1 business day.
                    </p>
                    <div className="ap-success-steps">
                      <p className="ap-success-steps-title">What happens next:</p>
                      {[
                        'We review your current processing setup and monthly volume',
                        'Your specialist prepares a custom savings comparison',
                        'You receive a no-pressure call to walk through your options',
                        'Same-day approval available once you decide to move forward',
                      ].map((s) => (
                        <div key={s} className="ap-success-step">
                          <svg className="ap-success-check" width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {s}
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: '13px', color: '#64748b' }}>
                      Questions?{' '}
                      <a href="tel:+13237840656" style={{ color: '#1d4ed8', fontWeight: 600 }}>
                        Call (323) 784-0656
                      </a>
                    </p>
                  </div>
                ) : (
                  <form noValidate onSubmit={handleSubmit}>
                    <h2 className="ap-form-title">Merchant Application</h2>
                    <p className="ap-form-subtitle">Tell us about your barbershop and we&apos;ll put together a custom rate proposal.</p>

                    {/* Section: Contact Info */}
                    <p className="ap-form-section-label">Contact Information</p>
                    <div className="ap-form-row">
                      <div>
                        <label className="ap-label" htmlFor="firstName">First Name <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          id="firstName"
                          type="text"
                          name="firstName"
                          required
                          autoComplete="given-name"
                          placeholder="Marcus"
                          className={`ap-input${errors.firstName ? ' error' : ''}`}
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                        {errors.firstName && <p className="ap-error-msg">{errors.firstName}</p>}
                      </div>
                      <div>
                        <label className="ap-label" htmlFor="lastName">Last Name <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          id="lastName"
                          type="text"
                          name="lastName"
                          required
                          autoComplete="family-name"
                          placeholder="Thompson"
                          className={`ap-input${errors.lastName ? ' error' : ''}`}
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                        {errors.lastName && <p className="ap-error-msg">{errors.lastName}</p>}
                      </div>
                    </div>

                    <div className="ap-form-row">
                      <div>
                        <label className="ap-label" htmlFor="phone">Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          id="phone"
                          type="tel"
                          name="phone"
                          required
                          autoComplete="tel"
                          placeholder="(555) 000-0000"
                          className={`ap-input${errors.phone ? ' error' : ''}`}
                          value={formData.phone}
                          onChange={handleChange}
                        />
                        {errors.phone && <p className="ap-error-msg">{errors.phone}</p>}
                      </div>
                      <div>
                        <label className="ap-label" htmlFor="email">Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          required
                          autoComplete="email"
                          placeholder="marcus@freshcutz.com"
                          className={`ap-input${errors.email ? ' error' : ''}`}
                          value={formData.email}
                          onChange={handleChange}
                        />
                        {errors.email && <p className="ap-error-msg">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Section: Business Info */}
                    <p className="ap-form-section-label" style={{ marginTop: '28px' }}>Business Information</p>
                    <div className="ap-form-group">
                      <label className="ap-label" htmlFor="businessName">Business Name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        id="businessName"
                        type="text"
                        name="businessName"
                        required
                        autoComplete="organization"
                        placeholder="Fresh Cutz Barbershop"
                        className={`ap-input${errors.businessName ? ' error' : ''}`}
                        value={formData.businessName}
                        onChange={handleChange}
                      />
                      {errors.businessName && <p className="ap-error-msg">{errors.businessName}</p>}
                    </div>

                    <div className="ap-form-group">
                      <label className="ap-label" htmlFor="businessWebsite">Business Website <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                      <input
                        id="businessWebsite"
                        type="url"
                        name="businessWebsite"
                        autoComplete="url"
                        placeholder="https://yourbarbershop.com"
                        className="ap-input"
                        value={formData.businessWebsite}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="ap-form-row">
                      <div>
                        <label className="ap-label" htmlFor="industry">Business Type <span style={{ color: '#ef4444' }}>*</span></label>
                        <select
                          id="industry"
                          name="industry"
                          required
                          className={`ap-input${errors.industry ? ' error' : ''}`}
                          value={formData.industry}
                          onChange={handleChange}
                        >
                          <option value="">Select type</option>
                          <option value="Barbershop">Barbershop</option>
                          <option value="Hair Salon">Hair Salon</option>
                          <option value="Nail Salon">Nail Salon</option>
                          <option value="Spa / Wellness">Spa / Wellness</option>
                          <option value="Tattoo / Piercing">Tattoo / Piercing</option>
                          <option value="Beauty Suite / Booth Rental">Beauty Suite / Booth Rental</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.industry && <p className="ap-error-msg">{errors.industry}</p>}
                      </div>
                      <div>
                        <label className="ap-label" htmlFor="monthlyVolume">Monthly Processing Volume <span style={{ color: '#ef4444' }}>*</span></label>
                        <select
                          id="monthlyVolume"
                          name="monthlyVolume"
                          required
                          className={`ap-input${errors.monthlyVolume ? ' error' : ''}`}
                          value={formData.monthlyVolume}
                          onChange={handleChange}
                        >
                          <option value="">Select range</option>
                          <option value="Under $5,000/mo">Under $5,000/mo</option>
                          <option value="$5,000 – $10,000/mo">$5,000 – $10,000/mo</option>
                          <option value="$10,000 – $25,000/mo">$10,000 – $25,000/mo</option>
                          <option value="$25,000 – $50,000/mo">$25,000 – $50,000/mo</option>
                          <option value="$50,000 – $100,000/mo">$50,000 – $100,000/mo</option>
                          <option value="Over $100,000/mo">Over $100,000/mo</option>
                        </select>
                        {errors.monthlyVolume && <p className="ap-error-msg">{errors.monthlyVolume}</p>}
                      </div>
                    </div>

                    <div className="ap-form-group">
                      <label className="ap-label" htmlFor="additionalNotes">Additional Notes <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                      <textarea
                        id="additionalNotes"
                        name="additionalNotes"
                        rows={4}
                        className="ap-input"
                        placeholder="Tell us about your shop, current processor, number of chairs, or anything else we should know..."
                        value={formData.additionalNotes}
                        onChange={handleChange}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={!isFormValid || loading}
                      className="ap-submit-btn"
                    >
                      {loading ? (
                        <>
                          <svg className="ap-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </>
                      )}
                    </button>

                    <p className="ap-form-legal">
                      By submitting you agree to our{' '}
                      <a href="/terms">Terms of Service</a> and{' '}
                      <a href="/privacy">Privacy Policy</a>.
                      We&apos;ll never share your information.
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* ── SIDEBAR COLUMN ──────────────────────────────────────────── */}
            <div className="ap-sidebar">

              {/* What Happens Next */}
              <div className="ap-next-card">
                <h3 className="ap-next-card-title">What Happens Next</h3>

                <div className="ap-step">
                  <div className="ap-step-num">1</div>
                  <div>
                    <div className="ap-step-title">We Review Your Application</div>
                    <div className="ap-step-desc">A ChargeAct specialist reviews your barbershop details and current processing setup.</div>
                  </div>
                </div>

                <div className="ap-step">
                  <div className="ap-step-num">2</div>
                  <div>
                    <div className="ap-step-title">Discovery Call</div>
                    <div className="ap-step-desc">We reach out within 1 business day to discuss your shop, volume, and potential savings.</div>
                  </div>
                </div>

                <div className="ap-step">
                  <div className="ap-step-num">3</div>
                  <div>
                    <div className="ap-step-title">Custom Rate Proposal</div>
                    <div className="ap-step-desc">You receive a transparent, no-obligation quote tailored to your barbershop&apos;s volume.</div>
                  </div>
                </div>

                <div className="ap-step">
                  <div className="ap-step-num">4</div>
                  <div>
                    <div className="ap-step-title">Get Approved &amp; Start Saving</div>
                    <div className="ap-step-desc">Same-day approval available. Start processing with lower rates as soon as tomorrow.</div>
                  </div>
                </div>
              </div>

              {/* Questions card */}
              <div className="ap-q-card">
                <h3 className="ap-q-card-title">Questions Before Applying?</h3>
                <p className="ap-q-card-p">Our team is happy to answer any questions before you submit. No pressure, no sales pitch.</p>
                <a href="tel:+13237840656" className="ap-q-card-phone">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.21.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z" />
                  </svg>
                  Call (323) 784-0656
                </a>
              </div>

              {/* Trust bar */}
              <div className="ap-trust-bar">
                <svg className="ap-trust-bar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <p className="ap-trust-bar-text">
                  <strong>Secure &amp; Confidential.</strong> Your information is encrypted and never sold to third parties.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* ── FOOTER ──────────────────────────────────────────────────────── */}
        <footer className="ap-footer">
          <div className="ap-footer-inner">
            <a href="/">
              <Image
                src="/assets/images/CHARGEACT_text_logo-1775097726221.png"
                alt="ChargeAct"
                width={120}
                height={28}
                className="ap-footer-logo"
              />
            </a>
            <div className="ap-footer-links">
              <a href="/" className="ap-footer-link">Home</a>
              <a href="/#solutions" className="ap-footer-link">Solutions</a>
              <a href="/#faq" className="ap-footer-link">FAQ</a>
              <a href="tel:+13237840656" className="ap-footer-link">(323) 784-0656</a>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <a href="/privacy" className="ap-footer-link">Privacy</a>
              <a href="/terms" className="ap-footer-link">Terms</a>
              <span className="ap-footer-copy">© 2025 ChargeAct</span>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
